/*
 * Enquiry-form progressive enhancement.
 *
 * The MH enquiry forms (.mh-form, rendered by components/forms/render.antlers.html)
 * are native Statamic {{ form:create }} POSTs. With JS off they submit normally
 * and the server 302s to the form's thank-you page (the tag's :redirect param,
 * resolved from the "Form Thank-You Pages" global). This handler layers on top:
 * it AJAX-submits, keeps the visitor in place, and on success routes to that same
 * thank-you URL via Swup (no full reload) — the server returns the URL as
 * `redirect` in its JSON response (mirrored onto the form's data-thank-you attr).
 * When no thank-you page is configured it degrades to an inline success message.
 * On a validation error it renders the server's messages inline so the js-only
 * modal forms surface errors without a reload that would close the modal.
 *
 * Delegated on document so it survives Swup content swaps without re-binding.
 */
import { navigateTo } from './router/swup.js';
import { pushEvent, pushPageView } from './signals/datalayer.js';
import { captureFormSubmission } from './signals/form-submission.js';
import { getRecaptchaToken, warmRecaptcha } from './recaptcha.js';

/**
 * Replace the form with an inline success message, mirroring the server-rendered
 * .mh-form__success block. Used only when no thank-you page is configured (the
 * degradation path). CSP-safe — textContent + createElement, no innerHTML.
 *
 * @param {HTMLFormElement} form
 */
function renderFormSuccess(form, created = true) {
    const box = document.createElement('div');
    box.className = 'mh-form__success';
    box.dataset.submissionCreated = String(created);
    box.dataset.conversionFired = 'true';
    box.setAttribute('role', 'status');
    box.setAttribute('aria-live', 'polite');
    const message = document.createElement('p');
    message.textContent = "Thanks — we've received your enquiry and will be in touch soon.";
    box.append(message);
    form.replaceChildren(box);
    box.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
}

/**
 * Render Statamic's validation messages inline, mirroring the server-rendered
 * .mh-form__errors block. CSP-safe — textContent + createElement, no innerHTML.
 *
 * @param {HTMLFormElement} form
 * @param {string[]} messages
 */
function renderFormErrors(form, messages) {
    let box = form.querySelector('.mh-form__errors');
    if (!box) {
        box = document.createElement('div');
        box.className = 'mh-form__errors';
        box.setAttribute('role', 'alert');
        box.setAttribute('aria-live', 'assertive');
        const fields = form.querySelector('.mh-form__fields');
        if (fields) {
            fields.before(box);
        } else {
            form.prepend(box);
        }
    }

    box.replaceChildren();
    const heading = document.createElement('p');
    heading.className = 'mh-form__errors-heading';
    heading.textContent = 'Please correct the following:';
    const list = document.createElement('ul');
    list.className = 'mh-form__errors-list';
    for (const message of messages) {
        const item = document.createElement('li');
        item.textContent = message;
        list.append(item);
    }
    box.append(heading, list);
    box.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
}

/**
 * The reCAPTCHA site key for a form, or '' when this environment has none.
 *
 * Server-rendered onto a .mh-form__recaptcha div inside the form rather than
 * onto the <form> itself — see the comment in components/forms/render.antlers.html
 * for why (Antlers only interpolates {{ config:… }} in ordinary markup).
 *
 * @param {HTMLFormElement} form
 * @returns {string}
 */
function recaptchaKeyFor(form) {
    return form.querySelector('[data-recaptcha-key]')?.dataset.recaptchaKey || '';
}

/**
 * Mint a reCAPTCHA token and write it into the form's hidden captcha_token
 * input, so the plain FormData below carries it. With no site key (reCAPTCHA
 * off for this environment) this is a no-op and RecaptchaVerifier fails open to
 * match.
 *
 * A failure to mint is deliberately NOT fatal here — we submit tokenless and let
 * the server decide. The alternative, blocking the submit, would turn a Google
 * outage or an over-zealous ad blocker into a form that silently refuses to send
 * with no server-side record of it.
 *
 * @param {HTMLFormElement} form
 */
async function attachRecaptchaToken(form) {
    const siteKey = recaptchaKeyFor(form);
    const field = form.querySelector('input[name="captcha_token"]');
    if (!siteKey || !field) return;

    try {
        field.value = (await getRecaptchaToken(siteKey)) ?? '';
    } catch {
        field.value = '';
    }
}

export function initEnquiryForms() {
    // Warm the script on first contact with any form field. Nothing is fetched
    // on a page the visitor only browses, which keeps Google's JS out of the
    // PageSpeed critical path; by the time anyone finishes typing, the script is
    // there and submit pays for the execute() call alone. Delegated + once, so
    // it survives Swup content swaps and fires exactly one load per page.
    document.addEventListener(
        'focusin',
        (e) => {
            const form = e.target.closest?.('form.mh-form');
            if (form) {
                warmRecaptcha(recaptchaKeyFor(form));
            }
        },
        { once: true },
    );

    document.addEventListener('submit', async (e) => {
        const form = e.target.closest('form.mh-form');
        if (!form) return;

        e.preventDefault();
        if (form.dataset.submitting) return;
        form.dataset.submitting = 'true';
        const tracking = captureFormSubmission(form);
        const formHandle = form.dataset.trackLabel;
        const submissionPath = window.location.pathname;
        const submitBtn = form.querySelector('[type="submit"]');
        submitBtn?.setAttribute('disabled', '');

        const release = () => {
            delete form.dataset.submitting;
            submitBtn?.removeAttribute('disabled');
        };

        // Before the FormData snapshot below — it reads input values once, so a
        // token written after it would never be sent.
        await attachRecaptchaToken(form);

        try {
            const response = await fetch(form.action || window.location.href, {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                },
                body: new FormData(form),
            });

            const data = await response.json().catch(() => null);

            // Honeypot rejections intentionally look successful to the sender,
            // but must never unlock results or produce conversion signals.
            if (response.ok && data?.success === true && data?.submission_created === false) {
                renderFormSuccess(form, false);
                return;
            }

            if (response.ok && data?.success === true && data?.submission_created === true) {
                if (submissionPath.replace(/\/$/, '') === '/home-designs/duplex') {
                    await import('./signals/pixels.js')
                        .then(({ trackDuplexConversion }) => trackDuplexConversion(formHandle, submissionPath))
                        .catch(() => {}); // Tracking failure must not block the thank-you page.
                }
                if (tracking) pushEvent('form_submit', tracking);
                // Close the global form modal (mounted outside #swup, so a Swup
                // navigate() won't tear it down) regardless of what follows.
                window.dispatchEvent(new CustomEvent('enquiry:success', { detail: { form } }));

                const target = data?.redirect || form.dataset.thankYou;
                if (target) {
                    // Release the lock BEFORE navigating: the persistent global
                    // modal form lives outside #swup and survives the Swup nav, so
                    // it must be submittable again for a second enquiry.
                    release();
                    window.Alpine?.store('toasts')?.success('Thanks — sending you through…');
                    navigateTo(target);
                } else {
                    // No thank-you page configured — degrade to inline success.
                    // Fire the conversion pageview here: there's no navigation to
                    // trigger the events.js /thank-you fallback on this path.
                    renderFormSuccess(form);
                    pushPageView(`${window.location.origin}/thank-you/`, 'Thank You');
                    window.Alpine?.store('toasts')?.success("Thanks — we've received your enquiry.");
                }
                return;
            }

            // Validation failure — Statamic returns 400 with { errors: [...] }.
            // Render them in place (a native re-POST would reload and, for the
            // js-only modal, close it, hiding the feedback entirely).
            if (response.status === 400 && Array.isArray(data?.errors) && data.errors.length) {
                renderFormErrors(form, data.errors);
                release();
                return;
            }

            // Rate limited (the statamic.forms limiter). Say so plainly rather
            // than "something went wrong" — this one is recoverable by waiting,
            // and the generic message invites an immediate retry that can only
            // fail again.
            if (response.status === 429) {
                renderFormErrors(form, [
                    "You've sent a few too many enquiries just now. Please wait a moment and try again.",
                ]);
                release();
                return;
            }

            // Any other failure — keep the visitor in place, let them retry.
            release();
            window.Alpine?.store('toasts')?.error('Something went wrong. Please try again.');
        } catch {
            // Network error — don't silently re-POST (risk of a double send).
            release();
            window.Alpine?.store('toasts')?.error('Something went wrong. Please try again.');
        }
    });
}
